import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from "../components/ui/dialog";
import {
  Church,
  MapPin,
  Users,
  ChevronRight,
  Loader2,
  ArrowLeft,
  Copy,
  Check,
  Pencil,
  Trash2,
  Mail,
  UserCircle,
  Clock
} from "lucide-react";
import { apiClient } from "../lib/apiClient";


export default function ChurchDetailPage() {
  const { churchId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [church, setChurch] = useState(null);
  const [groups, setGroups] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", address: "" });
  const [editLoading, setEditLoading] = useState(false);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [churchRes, groupsRes, membersRes] = await Promise.all([
        apiClient.get("church", churchId),
        apiClient.get("churchGroups", churchId),
        apiClient.get("churchMembers", churchId)
      ]);
      setChurch(churchRes.data);
      setGroups(groupsRes.data);
      setMembers(membersRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/login");
      } else if (err.response?.status === 404) {
        navigate("/churches");
      }
    } finally {
      setLoading(false);
    }
  }, [churchId, navigate]);  

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (!userData) {
      navigate("/login");
      return;
    }
    setUser(JSON.parse(userData));
    fetchData();
  }, [navigate, fetchData]);

  const isAdmin = user?.is_admin;

  const openEdit = () => {
    setEditForm({ name: church.name, address: church.address || "" });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await apiClient.patch("church", churchId, {
        name: editForm.name,
        address: editForm.address || null
      });
      setEditOpen(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update church");
    } finally {
      setEditLoading(false);
    }
  };

  const submitDelete = async () => {
    setDeleteLoading(true);
    try {
      await apiClient.delete("church", churchId);
      navigate("/churches");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete church");
    } finally {
      setDeleteLoading(false);
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-light flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sage" />
      </div>
    );
  }

  if (!church) {
    return (
      <div className="min-h-screen bg-stone-light flex items-center justify-center">
        <p className="text-stone-dark/60">Church not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-light noise-bg">
      {/* Navigation */}
      <nav className="bg-white border-b border-stone sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/churches"
              className="flex items-center gap-2 text-stone-dark/60 hover:text-stone-dark transition-colors"
              data-testid="back-to-churches"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Churches</span>
            </Link>
            <div className="h-6 w-px bg-stone" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-sage flex items-center justify-center">
                <Church className="w-4 h-4 text-white" />
              </div>
              <span className="font-serif font-semibold text-lg text-stone-dark">{church.name}</span>
              {isAdmin && (
                <button
                  onClick={openEdit}
                  className="p-1 rounded-md text-stone-dark/40 hover:text-sage hover:bg-sage/10 transition-colors"
                  title="Edit church"
                  data-testid="edit-church-btn"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={() => setDeleteOpen(true)}
              data-testid="delete-church-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          )}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Church Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-semibold text-stone-dark mb-2" data-testid="church-title">
            {church.name}
          </h1>
          {church.address && (
            <p className="text-stone-dark/60 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {church.address}
            </p>
          )}
          <div className="flex items-center gap-4 mt-3 text-sm text-stone-dark/50">
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {groups.length} group{groups.length !== 1 ? "s" : ""}
            </span>
            <span className="flex items-center gap-1">
              <UserCircle className="w-4 h-4" />
              {members.length} member{members.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Groups List */}
        <div className="mb-4">
          <h2 className="font-serif text-xl font-semibold text-stone-dark">Prayer Groups</h2>
        </div>

        {groups.length === 0 ? (
          <Card className="border-stone border-dashed">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 text-stone-dark/20 mx-auto mb-4" />
              <p className="text-stone-dark/60">No groups associated with this church yet</p>
              <p className="text-sm text-stone-dark/40 mt-1">
                When creating a group, select this church from the dropdown
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.map((group) => (
              <Link key={group.id} to={`/groups/${group.id}`}>
                <Card className="border-stone card-hover cursor-pointer h-full" data-testid={`church-group-card-${group.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-stone-dark">{group.name}</h3>
                        {group.description && (
                          <p className="text-xs text-stone-dark/50 mt-1 line-clamp-2">{group.description}</p>
                        )}
                        <p className="text-xs text-stone-dark/60 mt-2">
                          {group.member_count} member{group.member_count !== 1 ? "s" : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          onClick={(e) => { e.preventDefault(); copyInviteCode(group.invite_code); }}
                          data-testid={`copy-invite-${group.id}`}
                        >
                          {copiedCode === group.invite_code ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4 text-stone-dark/50" />
                          )}
                        </Button>
                        <ChevronRight className="w-4 h-4 text-stone-dark/30" />
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs bg-stone px-2 py-1 rounded font-mono">
                        {group.invite_code}
                      </span>
                      <span className="text-xs text-stone-dark/40">Invite code</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Members Section */}
        <div className="mt-10 mb-4">
          <h2 className="font-serif text-xl font-semibold text-stone-dark">Members</h2>
          <p className="text-sm text-stone-dark/50 mt-1">People who have associated their profile with this church</p>
        </div>

        {members.length === 0 ? (
          <Card className="border-stone border-dashed">
            <CardContent className="p-8 text-center">
              <UserCircle className="w-12 h-12 text-stone-dark/20 mx-auto mb-4" />
              <p className="text-stone-dark/60">No members yet</p>
              <p className="text-sm text-stone-dark/40 mt-1">
                Users can associate with this church from their profile settings
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {members.map((member) => (
              <Card key={member.id} className="border-stone" data-testid={`church-member-${member.id}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-sage/10 flex items-center justify-center shrink-0">
                    <UserCircle className="w-5 h-5 text-sage" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-stone-dark text-sm truncate">{member.name}</p>
                    <p className="text-xs text-stone-dark/50 flex items-center gap-1 truncate">
                      <Mail className="w-3 h-3 shrink-0" />
                      {member.email}
                    </p>
                    <p className="text-xs text-stone-dark/40 flex items-center gap-1 mt-0.5" data-testid={`member-last-login-${member.id}`}>
                      <Clock className="w-3 h-3 shrink-0" />
                      {member.last_login ? `Last login: ${new Date(member.last_login).toLocaleDateString()}` : "Never logged in"}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Edit Church Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">Edit Church</DialogTitle>
            <DialogDescription>Update church details</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="editChurchName">Church Name *</Label>
              <Input
                id="editChurchName"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
                data-testid="edit-church-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editChurchAddress">Address / Location</Label>
              <Input
                id="editChurchAddress"
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                data-testid="edit-church-address"
              />
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" className="rounded-full">Cancel</Button>
              </DialogClose>
              <Button
                type="submit"
                className="bg-sage hover:bg-sage-dark text-white rounded-full"
                disabled={editLoading}
                data-testid="save-church-edit-btn"
              >
                {editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Church Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-red-600">Delete Church</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{church.name}</strong>? This will disassociate all groups from this church. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" className="rounded-full">Cancel</Button>
            </DialogClose>
            <Button
              onClick={submitDelete}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
              disabled={deleteLoading}
              data-testid="confirm-delete-church-btn"
            >
              {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete Church"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
